'use client';

import { trackThemePreference } from '@workspace/analytics/posthog/events';
import type { AnalyticsApp } from '@workspace/analytics/posthog/types';
import {
  DEFAULT_THEME_STORAGE_KEY,
  getThemePreferenceCookie,
  isThemePreference,
  setThemePreferenceCookie,
  THEME_COOKIE_KEY,
} from '@workspace/ui/lib/theme-preference';
import {
  ThemeProvider as NextThemesProvider,
  useTheme,
  type ThemeProviderProps,
} from 'next-themes';
import { createContext, use, useEffect, useRef } from 'react';

interface AppThemeProviderProps extends ThemeProviderProps {
  analyticsApp?: AnalyticsApp;
}

const ThemeAnalyticsContext = createContext<AnalyticsApp | undefined>(undefined);

export function useThemeAnalyticsApp(): AnalyticsApp | undefined {
  return use(ThemeAnalyticsContext);
}

/**
 * next-themes stores the theme in localStorage, which is per-origin and not shared
 * across subdomains. We mirror the theme into a cross-subdomain cookie so a choice
 * made in one app (e.g. wallet) is reflected in the others (e.g. eor).
 *
 * The inline script copies that cookie into localStorage before next-themes' own
 * script runs, avoiding a flash. Both scripts are rendered with type="application/json"
 * on the client so React 19 doesn't warn about a <script> in the tree — they already
 * executed during SSR. @see https://github.com/pacocoursey/next-themes/issues/385
 */
export function ThemeProvider({ children, analyticsApp, ...props }: AppThemeProviderProps) {
  const scriptProps =
    typeof window === 'undefined' ? undefined : ({ type: 'application/json' } as const);
  const storageKey = props.storageKey ?? DEFAULT_THEME_STORAGE_KEY;

  return (
    <>
      <script
        {...scriptProps}
        suppressHydrationWarning
        dangerouslySetInnerHTML={{ __html: getThemeStorageSyncScript(storageKey) }}
      />
      <ThemeAnalyticsContext value={analyticsApp}>
        <NextThemesProvider scriptProps={scriptProps} storageKey={storageKey} {...props}>
          <ThemeCookieSync storageKey={storageKey} analyticsApp={analyticsApp} />
          <ThemeAnalyticsSync analyticsApp={analyticsApp} />
          {children}
        </NextThemesProvider>
      </ThemeAnalyticsContext>
    </>
  );
}

function ThemeCookieSync({
  storageKey,
  analyticsApp,
}: {
  storageKey: string;
  analyticsApp?: AnalyticsApp;
}) {
  const { setTheme, theme } = useTheme();
  const hasSyncedInitialTheme = useRef(false);

  useEffect(() => {
    try {
      if (!hasSyncedInitialTheme.current) {
        hasSyncedInitialTheme.current = true;

        const cookieTheme = getThemePreferenceCookie();
        if (cookieTheme) {
          window.localStorage.setItem(storageKey, cookieTheme);
          if (theme !== cookieTheme) setTheme(cookieTheme);

          if (analyticsApp) {
            trackThemePreference({
              app: analyticsApp,
              theme: cookieTheme,
              source: 'cookie_sync',
            });
          }

          return;
        }
      }

      if (isThemePreference(theme)) setThemePreferenceCookie(theme);
    } catch (error) {
      console.warn('Failed to sync theme preference:', error);
    }
  }, [analyticsApp, setTheme, storageKey, theme]);

  return null;
}

function ThemeAnalyticsSync({ analyticsApp }: { analyticsApp?: AnalyticsApp }) {
  const { resolvedTheme } = useTheme();
  const hasTrackedInitialTheme = useRef(false);

  useEffect(() => {
    if (!analyticsApp || hasTrackedInitialTheme.current) return;
    if (resolvedTheme !== 'light' && resolvedTheme !== 'dark') return;
    if (getThemePreferenceCookie()) return;

    hasTrackedInitialTheme.current = true;
    trackThemePreference({
      app: analyticsApp,
      theme: resolvedTheme,
      source: 'initial',
    });
  }, [analyticsApp, resolvedTheme]);

  return null;
}

// JSON.stringify leaves `<` untouched, so a value containing `</script>` could close
// the inline script tag. Escaping `<` to its `\u003c` form keeps the value valid JS
// while making a tag breakout impossible.
function serializeForInlineScript(value: string): string {
  return JSON.stringify(value).replace(/</g, '\\u003c');
}

function getThemeStorageSyncScript(storageKey: string): string {
  return `
    try {
      var cookieKey = ${serializeForInlineScript(THEME_COOKIE_KEY)};
      var storageKey = ${serializeForInlineScript(storageKey)};
      var cookieTheme = document.cookie
        .split('; ')
        .find(function (cookie) {
          return cookie.indexOf(cookieKey + '=') === 0;
        });

      if (cookieTheme) {
        var theme = decodeURIComponent(cookieTheme.split('=').slice(1).join('='));

        if (theme === 'light' || theme === 'dark') {
          localStorage.setItem(storageKey, theme);
        }
      }
    } catch (error) {}
  `;
}
